import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { TagComponent, TagComponentType } from 'shared/components/tag/tag.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UploadComponent } from 'shared/components/upload/upload.component';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import {
  AccountDocument,
  Statuses,
  DocumentTypes,
  IDocument,
  IDocuments
} from 'shared/types/payment-account.interface';
import { PaymentAccountsService } from 'core/services/payment-accounts.service';
import { catchError, finalize, forkJoin, of, switchMap, throwError } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs/operators';
import { KorviuLoaderComponent } from 'shared/components/korviu-loader/korviu-loader.component';
import { AlertComponent } from 'shared/components/alert/alert.component';
import { NavigationService } from 'core/services/navigation.service';
import { CustomMessageService } from 'core/services/custom-message.service';

interface IBlockStatus {
  localizableName: string;
  type: TagComponentType;
  status: string;
}

@Component({
  selector: 'app-documentation',
  standalone: true,
  imports: [
    TagComponent,
    TranslateModule,
    UploadComponent,
    KorviuButtonComponent,
    NzIconDirective,
    NgTemplateOutlet,
    NzButtonComponent,
    KorviuLoaderComponent,
    AlertComponent
  ],
  templateUrl: './documentation.component.html',
  styleUrl: './documentation.component.scss'
})
export class DocumentationComponent implements OnInit {
  private _paymentAccountsService = inject(PaymentAccountsService);
  private _translateService = inject(TranslateService);
  private _navigationService = inject(NavigationService);
  private _destroyRef = inject(DestroyRef);
  private _messageService = inject(CustomMessageService);
  private _document = inject(DOCUMENT);

  @Input() idEdit: boolean;
  @Input() taxEdit: boolean;
  @Input() isAdding: boolean = true;
  @Input() collapsable: boolean = false;
  @Input() documentation: IDocuments;
  @Input() isUsAccount: boolean;
  @Output() backClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() submitClick: EventEmitter<void> = new EventEmitter<void>();

  public isLoading = signal<boolean>(false);
  public readonly openingDocumentId = signal<number | null>(null);

  public DocumentTypes: typeof DocumentTypes = DocumentTypes;

  public get officialIdFilesSubmitted(): boolean {
    return this.getBlockStatus(DocumentTypes.ID).status === Statuses.SUBMITTED;
  }
  public get taxStatusFilesSubmitted(): boolean {
    return this.getBlockStatus(DocumentTypes.TAX).status === Statuses.SUBMITTED;
  }

  public get officialIdEditMode(): boolean {
    return (
      this.documentation?.officialId?.some(d => d.status === 'DECLINED') ||
      this.documentation?.officialId?.some(d => !d.id) ||
      !this.documentation?.officialId?.length ||
      this.idEdit
    );
  }
  public get taxEditMode(): boolean {
    return (
      this.documentation?.tax?.some(d => d.status === 'DECLINED') ||
      this.documentation?.tax?.some(d => !d.id) ||
      !this.documentation?.tax?.length ||
      this.taxEdit
    );
  }

  public get officialIdShowLinks(): boolean {
    const firstDocument = this.documentation?.officialId?.[0];
    return (
      !this.isAdding &&
      !this.officialIdEditMode &&
      this.documentation?.officialId?.every(d => d.status === firstDocument.status)
    );
  }
  public get taxShowLinks(): boolean {
    const firstDocument = this.documentation?.tax?.[0];
    return (
      !this.isAdding && !this.taxEditMode && this.documentation?.tax?.every(d => d.status === firstDocument.status)
    );
  }

  public get hideButtons(): boolean {
    return !this.isAdding;
  }

  public get officialIdStatuses(): any[] {
    const exist = id => this.documentation?.officialIdFiles?.find(f => f?.id === id);
    const newFiles = this.documentation?.officialIdFiles?.filter(f => f?.uid).map(f => ({ id: f.id }));

    return [
      ...(this.documentation?.officialId ?? [])
        ?.filter((d: IDocument) => exist(d.file?.id))
        .map((d: IDocument) => ({ status: d?.status, id: d.id })),
      ...newFiles
    ];
  }
  public get taxStatuses(): any[] {
    const exist = id => this.documentation?.taxFiles?.find(f => f?.id === id);
    const newFiles = this.documentation?.taxFiles?.filter(f => f?.uid).map(f => ({ id: f.id }));

    return [
      ...(this.documentation?.tax ?? [])
        ?.filter((d: IDocument) => exist(d.file?.id))
        .map((d: IDocument) => ({ status: d?.status, id: d.id })),
      ...newFiles
    ];
  }

  public get formIsValid(): boolean {
    return this.isUsAccount
      ? this.officialIdFilesSubmitted
      : this.officialIdFilesSubmitted && this.taxStatusFilesSubmitted;
  }

  public officialIdCollapsed = signal<boolean>(false);
  public taxStatusCollapsed = signal<boolean>(false);

  private readonly _openedDocumentUrls = new Set<string>();
  private _documentWindow: Window | null = null;

  constructor() {
    this._destroyRef.onDestroy(() => {
      this._documentWindow?.close();
      this._openedDocumentUrls.forEach(url => URL.revokeObjectURL(url));
    });
  }

  public ngOnInit(): void {
    if (this.isAdding) {
      // this._prefillApprovedFiles();
      this._navigationService.goBackCustomAction = this.onBackClick.bind(this);
    }
  }

  public onOfficialIdFileChange(list: any): void {
    this.documentation.officialIdFiles = list;
    this.documentation.officialId = list.map(file => ({
      file,
      name: file.name,
      url: '',
      id: typeof file.id === 'number' ? file.id : '',
      status: this.documentation.officialId?.find(d => d.file.id === file.id)?.status
    }));
  }

  public onTaxFileChange(list: any): void {
    this.documentation.taxFiles = list;
    this.documentation.tax = list.map(file => ({
      file,
      name: file.name,
      url: '',
      id: typeof file.id === 'number' ? file.id : '',
      status: this.documentation.tax?.find(d => d.file.id === file.id)?.status
    }));
  }

  public getDeclinedReasons(documentType: DocumentTypes): { documentName: string; reason: string; id: number }[] {
    const documentTypeProperty = documentType === DocumentTypes.ID ? 'officialId' : 'tax';
    const rejectReasons = this.documentation[documentTypeProperty]?.filter(d => !!d.rejectReason?.id) ?? [];
    const rejectReasonLanguage = this._translateService.currentLang === 'en' ? 'name_en' : 'name_es';
    return rejectReasons.map(d => ({
      documentName: d.name,
      reason: d.rejectReason[rejectReasonLanguage],
      id: d.rejectReason.id
    }));
  }

  private _prefillApprovedFiles(): void {
    this.isLoading.set(true);
    this._paymentAccountsService
      .getUserDocuments([{ name: 'user_document_status', value: Statuses.APPROVED }])
      .pipe(
        switchMap((response: AccountDocument[]) => {
          const officialIdApproved = response.filter(
            (d: AccountDocument) => d.user_document_type.code === DocumentTypes.ID
          );
          const taxApproved = response.filter((d: AccountDocument) => d.user_document_type.code === DocumentTypes.TAX);

          if (
            (!officialIdApproved?.length && this.isUsAccount) ||
            (!officialIdApproved?.length && !taxApproved?.length && !this.isUsAccount)
          ) {
            return throwError(null);
          }

          const [prevLastOfficialIdApproved, lastOfficialIdApproved] = officialIdApproved;
          const [lastTaxApproved] = taxApproved;

          return forkJoin([
            this._paymentAccountsService.getImageFile(
              prevLastOfficialIdApproved?.url,
              prevLastOfficialIdApproved?.file_name
            ),
            this._paymentAccountsService.getImageFile(lastOfficialIdApproved?.url, lastOfficialIdApproved?.file_name),
            this._paymentAccountsService.getImageFile(lastTaxApproved?.url, lastTaxApproved?.file_name),
            of([prevLastOfficialIdApproved?.id, lastOfficialIdApproved?.id, lastTaxApproved?.id])
          ]);
        }),
        tap(
          ([
            prevLastOfficialIdApprovedFile,
            lastOfficialIdApprovedFile,
            lastTaxApprovedFile,
            [prevLastOfficialIdApprovedId, lastOfficialIdApprovedId, lastTaxApprovedId]
          ]) => {
            if (!this.documentation?.officialIdFiles?.length) {
              prevLastOfficialIdApprovedFile.id = prevLastOfficialIdApprovedId;
              lastOfficialIdApprovedFile.id = lastOfficialIdApprovedId;
              this.documentation.officialIdFiles = [prevLastOfficialIdApprovedFile, lastOfficialIdApprovedFile];
              this.documentation.officialId = [
                {
                  file: prevLastOfficialIdApprovedFile,
                  status: 'APPROVED',
                  name: prevLastOfficialIdApprovedFile.name,
                  url: '',
                  id: prevLastOfficialIdApprovedId
                },
                {
                  file: lastOfficialIdApprovedFile,
                  status: 'APPROVED',
                  name: lastOfficialIdApprovedFile.name,
                  url: '',
                  id: lastOfficialIdApprovedId
                }
              ];
            }
            if (!this.documentation?.taxFiles?.length && !this.isUsAccount) {
              lastTaxApprovedFile.id = lastTaxApprovedId;
              this.documentation.taxFiles = [lastTaxApprovedFile];
              this.documentation.tax = [
                {
                  file: lastTaxApprovedFile,
                  status: 'APPROVED',
                  name: lastTaxApprovedFile.name,
                  url: '',
                  id: lastTaxApprovedId
                }
              ];
            }
          }
        ),
        finalize(() => {
          this.isLoading.set(false);
        }),
        catchError(e => of(null)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  public getBlockStatus(type: DocumentTypes): IBlockStatus {
    if (type === DocumentTypes.ID) {
      return this.getStatus(
        this.documentation?.officialIdFiles,
        this.officialIdStatuses,
        this.documentation?.officialId,
        1
      );
    }
    if (type === DocumentTypes.TAX) {
      return this.getStatus(this.documentation?.taxFiles, this.taxStatuses, this.documentation?.tax, 1);
    }

    return { localizableName: 'profile.addAccount.required', type: 'error', status: Statuses.REQUIRED };
  }

  private getStatus(files: any[], statuses: any[], documents: IDocument[], requiredFilesLength: number): IBlockStatus {
    const approvedStatus: IBlockStatus = {
      localizableName: 'profile.approved',
      type: 'success',
      status: Statuses.APPROVED
    };
    const reviewingStatus: IBlockStatus = {
      localizableName: 'profile.reviewing',
      type: 'warning',
      status: Statuses.REVIEWING
    };
    const declinedStatus: IBlockStatus = {
      localizableName: 'profile.declined',
      type: 'error',
      status: Statuses.DECLINED
    };
    const completedStatus: IBlockStatus = {
      localizableName: 'profile.submitted',
      type: 'info',
      status: Statuses.SUBMITTED
    };
    const requiredStatus: IBlockStatus = {
      localizableName: 'profile.addAccount.required',
      type: 'error',
      status: Statuses.REQUIRED
    };

    if (files?.length && files?.length >= requiredFilesLength) {
      const everyStatusIsApproved = statuses.every(status => status?.status === 'APPROVED');

      if (files?.length >= requiredFilesLength && everyStatusIsApproved) {
        return approvedStatus;
      } else {
        if (documents.some(d => d.status === 'DECLINED')) {
          return declinedStatus;
        } else if (documents.every(d => d.status === 'REVIEWING' || d.status === 'APPROVED')) {
          return reviewingStatus;
        }
        return completedStatus;
      }
    } else {
      return requiredStatus;
    }
  }

  public onOfficialIdCollapseClick(): void {
    this.officialIdCollapsed.set(!this.officialIdCollapsed());
  }

  public onTaxStatusCollapseClick(): void {
    this.taxStatusCollapsed.set(!this.taxStatusCollapsed());
  }

  public openDocument(document: IDocument): void {
    if (this.openingDocumentId() !== null) {
      return;
    }
    if (!document.id) {
      this._messageService.error(this._translateService.instant('profile.addAccount.documentOpenError'));
      return;
    }

    const previewWindow = this._document.defaultView?.open('', '_blank');
    if (!previewWindow) {
      this._messageService.error(this._translateService.instant('profile.addAccount.documentPopupBlocked'));
      return;
    }
    previewWindow.opener = null;
    this._documentWindow = previewWindow;
    this.openingDocumentId.set(document.id);

    this._paymentAccountsService
      .getDocumentFile(
        { id: document.id, url: document.url, file_name: document.name, url_expires_at: document.url_expires_at },
        true
      )
      .pipe(
        finalize(() => {
          this.openingDocumentId.set(null);
          this._documentWindow?.close();
          this._documentWindow = null;
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: file => {
          if (!previewWindow.closed) {
            const url = URL.createObjectURL(file);
            this._openedDocumentUrls.add(url);
            previewWindow.location.replace(url);
          }
          this._documentWindow = null;
        },
        error: () => {
          this._messageService.error(this._translateService.instant('profile.addAccount.documentOpenError'));
        }
      });
  }

  public onBackClick(): void {
    this.backClick.emit();
  }

  public onSubmitClick(): void {
    this.submitClick.emit();
  }
}
